import React from "react";

import { Page, StyleSheet, View } from "@react-pdf/renderer";
import { differenceInDays } from "date-fns";
import { sum } from "lodash";

import { graphql } from "@/gql";
import {
  PdfMealPatternFragment,
  PdfMealPlanSectionFragment,
  PdfMealTemplate_HeaderFragment,
} from "@/gql/graphql";
import { colors } from "@/theme/overrides/colors";
import { formatISODate, parseDate } from "@/utils/datetime";
import { mealPatternMultiplier, mealPatternNameLabel } from "@/utils/food";
import { pluralize } from "@/utils/string";
import { FragmentType } from "@/utils/type";

import ContentHeader from "./components/ContentHeader";
import DayIndicator from "./components/DayIndicator";
import Footer from "./components/Footer";
import Section from "./components/Section";
import Subheader from "./components/Subheader";

const mealPatternFragment = graphql(`
  fragment PDFMealPattern on MealPlan {
    plannedMeals {
      id
      meal
      recommendedTime
      mealPatternItems {
        id
        name
        amountServings
      }
    }
  }
`);

// Create styles
const styles = StyleSheet.create({
  page: {
    padding: "0px",
    fontFamily: "Mulish",
    color: colors.dark,
    fontSize: "16px",
  },
  container: {
    padding: "12px 24px 0px 24px",
  },
});

interface Props {
  headerData: PdfMealTemplate_HeaderFragment;
  data: FragmentType<typeof mealPatternFragment>;
}

const PDFMealPattern: React.FC<Props> = ({ headerData, data }) => {
  const sectionData = data.plannedMeals.map(toSectionData);
  const totalCalories = sum(
    sectionData.map((s) => s.itemsByDate[0].totalEnergyKcal)
  );
  const endDay =
    differenceInDays(
      parseDate(headerData.endDate),
      parseDate(headerData.startDate)
    ) + 1;

  return (
    <Page size="A4" style={styles.page}>
      <Subheader data={headerData} title="Meal Pattern" noCalories />
      <View style={styles.container}>
        <DayIndicator day={`Day 1 - Day ${endDay}`} />
        <ContentHeader
          data={headerData.mealPlan}
          totalCalories={totalCalories}
        />
        {sectionData.map((plannedMeal, index) => (
          <Section
            key={index}
            data={plannedMeal}
            macronutrients={headerData.mealPlan}
            day={1}
          />
        ))}
        <Footer />
      </View>
    </Page>
  );
};

function toSectionData(
  pm: PdfMealPatternFragment["plannedMeals"][0]
): PdfMealPlanSectionFragment {
  const totalCalories = pm.mealPatternItems.reduce(
    (sum2, i) =>
      sum2 + mealPatternMultiplier(i.name).calories * i.amountServings,
    0
  );

  return {
    __typename: "PlannedMeal",
    id: "fake_ass_id",
    meal: pm.meal,
    recommendedTime: pm.recommendedTime,
    itemsByDate: [
      {
        __typename: "PlannedMealItemsByDate",
        date: formatISODate(new Date()),
        totalEnergyKcal: totalCalories,
        recipes: [],
        items: pm.mealPatternItems.map((i) => ({
          __typename: "PlannedMealItem",
          id: "fake_ass_id",
          food: {
            __typename: "Food",
            id: "fake_ass_id",
            name: mealPatternNameLabel(i.name),
          },
          foodPortion: {
            __typename: "FoodPortion",
            id: "fake_ass_id",
            unitWithModifier: pluralize("serving", i.amountServings),
          },
          amount: i.amountServings,
          energyKcal: mealPatternMultiplier(i.name).calories * i.amountServings,
        })),
      },
    ],
  };
}

export default PDFMealPattern;
